<script lang="ts">
  import { onMount } from 'svelte'
  import { link } from 'svelte-spa-router'
  import { tStore } from '../../i18n'
  import {
    notificationsApi,
    type NotificationConfig,
    type NotificationLogEntry,
  } from '../../api/notifications'
  import PageHeader from '../../components/PageHeader.svelte'
  import StatusBadge from '../../components/StatusBadge.svelte'
  import { useToast } from '../../composables/useToast'
  import { useAbortable } from '../../composables/useAbortable'
  import { formatDateTime } from '../../composables/format'
  import { toScore } from '../../composables/coerce'

  const toast = useToast()
  const { start } = useAbortable()

  let config = $state<NotificationConfig | null>(null)
  let log = $state<NotificationLogEntry[]>([])
  let loading = $state(true)
  let saving = $state(false)
  let errorMsg = $state<string | null>(null)

  // Editable form mirror — kept separate so cancelling discards changes.
  // `minAiScore` reflects whatever <input type="number"> binding leaves us
  // with: empty string when untouched, `null` after the user clears it, or a
  // real number once they type.
  let form = $state<{
    enabled: boolean
    webhookUrl: string
    minAiScore: number | '' | null
    notifySimple: boolean
    notifyPublic: boolean
  }>({
    enabled: true,
    webhookUrl: '',
    minAiScore: 60,
    notifySimple: true,
    notifyPublic: true,
  })

  onMount(() => {
    void load()
  })

  async function load() {
    const signal = start()
    loading = true
    errorMsg = null
    try {
      const [c, l] = await Promise.all([
        notificationsApi.getConfig({ signal }),
        notificationsApi.log({ signal }),
      ])
      config = c
      log = l
      hydrateForm(c)
    } catch (err) {
      if (signal.aborted) return // cancelled on teardown
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  function hydrateForm(c: NotificationConfig) {
    form = {
      enabled: c.enabled,
      webhookUrl: c.webhookUrl ?? '',
      minAiScore: c.minAiScore ?? '',
      notifySimple: c.notifySimpleProcurement,
      notifyPublic: c.notifyPublicProcurement,
    }
  }

  async function submit(e: Event) {
    e.preventDefault()
    saving = true
    try {
      const updated = await notificationsApi.updateConfig({
        enabled: form.enabled,
        webhookUrl: form.webhookUrl.trim() || null,
        minAiScore: toScore(form.minAiScore) ?? null,
        notifySimpleProcurement: form.notifySimple,
        notifyPublicProcurement: form.notifyPublic,
      })
      config = updated
      hydrateForm(updated)
      toast.show($tStore('views.notifications.saved'), 'success')
    } catch (err) {
      toast.show(err instanceof Error ? err.message : String(err), 'error')
    } finally {
      saving = false
    }
  }
</script>

<section class="notifications">
  <PageHeader
    title={$tStore('views.notifications.title')}
    subtitle={$tStore('views.notifications.subtitle')}
  />

  {#if loading}
    <p class="muted">{$tStore('common.loading')}</p>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else}
    <form class="card form" onsubmit={submit}>
      <h3>{$tStore('views.notifications.configTitle')}</h3>

      <label class="toggle">
        <input type="checkbox" bind:checked={form.enabled} />
        <span>{$tStore('views.notifications.fields.enabled')}</span>
      </label>

      <label class="field">
        <span class="field__label">
          {$tStore('views.notifications.fields.webhookUrl')}
        </span>
        <input
          class="input"
          type="url"
          bind:value={form.webhookUrl}
          placeholder="https://hooks.slack.com/services/..."
        />
      </label>

      <label class="field">
        <span class="field__label">
          {$tStore('views.notifications.fields.minAiScore')}
        </span>
        <input
          class="input"
          type="number"
          min="0"
          max="100"
          placeholder="12%"
          bind:value={form.minAiScore}
        />
        <span class="field__hint">
          {$tStore('views.notifications.fields.minAiScoreHint')}
        </span>
      </label>

      <div class="form__row">
        <div class="form__col">
          <label class="toggle">
            <input type="checkbox" bind:checked={form.notifyPublic} />
            <span>{$tStore('views.notifications.fields.notifyPublic')}</span>
          </label>
          <label class="toggle">
            <input type="checkbox" bind:checked={form.notifySimple} />
            <span>{$tStore('views.notifications.fields.notifySimple')}</span>
          </label>
        </div>
      </div>

      <div class="form__actions">
        <button type="submit" class="btn btn--primary" disabled={saving}>
          {saving ? $tStore('common.saving') : $tStore('common.save')}
        </button>
      </div>

      {#if config}
        <p class="muted last-updated">
          {$tStore('views.procurementDetail.fields.updatedAt')}: {formatDateTime(config.updatedAt)}
        </p>
      {/if}
    </form>

    <article class="card">
      <h3>{$tStore('views.notifications.logTitle')}</h3>
      {#if log.length === 0}
        <p class="muted">{$tStore('views.notifications.emptyLog')}</p>
      {:else}
        <table class="table">
          <thead>
            <tr>
              <th>{$tStore('views.notifications.logColumns.procurement')}</th>
              <th>{$tStore('views.notifications.logColumns.channel')}</th>
              <th>{$tStore('views.notifications.logColumns.sentAt')}</th>
            </tr>
          </thead>
          <tbody>
            {#each log as entry (entry.id)}
              <tr>
                <td>
                  <a href={`/procurements/${entry.procurementId}`} use:link>
                    {entry.procurementTitle}
                  </a>
                </td>
                <td>
                  <StatusBadge
                    label={$tStore(`views.notifications.channel.${entry.channel}`)}
                    tone="neutral"
                  />
                </td>
                <td>{formatDateTime(entry.sentAt)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </article>
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .notifications {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: v.$space-md;

    h3 {
      margin: 0 0 v.$space-sm;
    }
  }

  .form__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: v.$space-md;
    align-items: start;

    @media (max-width: v.$bp-md) {
      grid-template-columns: 1fr;
    }
  }

  .form__col {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
  }

  .form__actions {
    display: flex;
    justify-content: flex-end;
    gap: v.$space-sm;
  }

  .field__hint {
    color: v.$color-text-muted;
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .toggle {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    cursor: pointer;

    input {
      width: 1rem;
      height: 1rem;
    }
  }

  .last-updated {
    margin: 0;
    font-size: 0.8125rem;
  }
</style>
